import { Container, Graphics, Text, TextStyle, Assets, Texture, Sprite } from 'pixi.js';
import { Button } from './Button';
import { ImageButton } from './ImageButton';
import { InfoPopup } from './InfoPopup';
import { Notification } from './Notification';
import { HistoryPopup } from './HistoryPopup';
import { AutoSpinPopup } from './AutoSpinPopup';
import { COLORS, FONTS } from '../config/Colors';
import { AudioManager } from '../utils/AudioManager';
import type { Pinia } from 'pinia';
import { useGameStore } from '../stores/gameStore';

export class UI {
  private container: Container;
  private particleContainer: Container;
  private gameStore: ReturnType<typeof useGameStore>;
  private uiTextures: Map<string, Texture> = new Map();
  private frameSprite: Sprite | null = null;
  private backgroundSprite: Sprite | null = null;
  
  private balanceText!: Text;
  private betText!: Text;
  private winText!: Text;
  private freeSpinsText!: Text;
  
  private spinButton!: Button | ImageButton;
  private betUpButton!: Button | ImageButton;
  private betDownButton!: Button | ImageButton;
  private autoSpinButton!: Button | ImageButton;
  private infoButton!: Button | ImageButton;
  private historyButton!: Button | ImageButton;
  private infoPopup!: InfoPopup;
  private historyPopup!: HistoryPopup;
  private autoSpinPopup!: AutoSpinPopup;
  private notification!: Notification;
  
  private background!: Graphics;
  private frame!: Graphics;
  private reelBackground!: Graphics;
  
  private audioManager: AudioManager;
  
  private onSpinCallback?: () => void;
  private onBetUpCallback?: () => void;
  private onBetDownCallback?: () => void;
  private onAutoSpinCallback?: (rounds: number) => void;
  private onStopAutoSpinCallback?: () => void;
  
  private isAutoSpinning: boolean = false;
  private autoSpinAnimationTime: number = 0;
  
  constructor(container: Container, pinia: Pinia) {
    this.container = container;
    this.audioManager = AudioManager.getInstance();
    this.gameStore = useGameStore(pinia);
    
    this.particleContainer = new Container();
    this.background = new Graphics();
    this.frame = new Graphics();
    this.reelBackground = new Graphics();
    
    // Create background graphics (fallback)
    this.createBackground();
    // Background sprite will be created asynchronously
    this.createBackgroundAsync();
    // Frame will be created asynchronously
    this.createFrameAsync();
    
    this.container.addChild(this.background);
    this.container.addChild(this.frame);
    
    this.createTexts();
    // Buttons will be created asynchronously
    this.createButtonsAsync();
    this.createInfoPopup();
    this.createHistoryPopup();
    this.createAutoSpinPopup();
    this.createNotification();
  }
  
  private async createBackgroundAsync(): Promise<void> {
    await this.loadBackgroundTexture();
  }
  
  private async loadBackgroundTexture(): Promise<void> {
    try {
      const bgTexture = await Assets.load('/assets/ui-2/game-bg.png');
      this.uiTextures.set('background', bgTexture);
      
      // Create sprite for game background
      this.backgroundSprite = new Sprite(bgTexture);
      this.backgroundSprite.anchor.set(0.5);
      this.backgroundSprite.x = 0;
      this.backgroundSprite.y = 0;
      this.backgroundSprite.zIndex = -1; // Behind everything
      
      // Scale to fit the game area (768x1024)
      const scaleX = 768 / bgTexture.width;
      const scaleY = 1024 / bgTexture.height;
      this.backgroundSprite.scale.set(Math.max(scaleX, scaleY));
      
      // Hide the graphics background
      this.background.visible = false;
      
      this.container.addChildAt(this.backgroundSprite, 0); // Add at the very back
      console.log('✓ Game background sprite created and added');
      console.log(`  Background size: ${this.backgroundSprite.width.toFixed(0)} x ${this.backgroundSprite.height.toFixed(0)}`);
    } catch (error) {
      console.error('Failed to load game background texture:', error);
      // Fall back to graphics background (already created)
    }
  }
  
  private async createFrameAsync(): Promise<void> {
    await this.loadFrameTexture();
  }
  
  private async loadFrameTexture(): Promise<void> {
    try {
      const frameTexture = await Assets.load('/assets/ui-2/bord-frame.png');
      this.uiTextures.set('frame', frameTexture);
      
      // Create sprite for frame background
      this.frameSprite = new Sprite(frameTexture);
      this.frameSprite.anchor.set(0.5);
      this.frameSprite.x = 0;
      this.frameSprite.y = -50; // Move up by 50 pixels
      this.frameSprite.zIndex = 100; // On top of symbols
      
      // Scale to cover reel area (make it larger)
      // Original: 404x253, we want it to be about 700x440
      const scaleX = 700 / frameTexture.width;
      const scaleY = 440 / frameTexture.height;
      this.frameSprite.scale.set(Math.min(scaleX, scaleY));
      
      // Make sure frame is visible on top
      this.frameSprite.alpha = 1;
      
      this.container.addChild(this.frameSprite);
      console.log('✓ Frame background sprite created and added (zIndex: 100)');
      console.log(`  Frame size: ${this.frameSprite.width.toFixed(0)} x ${this.frameSprite.height.toFixed(0)}`);
      console.log(`  Frame position: (${this.frameSprite.x.toFixed(0)}, ${this.frameSprite.y.toFixed(0)})`);
    } catch (error) {
      console.error('Failed to load frame texture:', error);
      // Fall back to graphics frame
      this.createFrameGraphics();
    }
  }
  
  private createFrameGraphics(): void {
    // Make frame larger to cover reels
    const frameWidth = 700;
    const frameHeight = 440;
    
    this.frame.clear();
    
    // Position frame up by 50 pixels to match sprite position
    this.frame.y = -50;
    
    // Outer frame - very dark brown (traditional wood)
    this.frame.roundRect(-frameWidth/2 - 15, -frameHeight/2 - 15, frameWidth + 30, frameHeight + 30, 20);
    this.frame.fill({ color: 0x3E2723 }); // Very dark brown
    
    // Inner frame - dark brown
    this.frame.roundRect(-frameWidth/2 - 8, -frameHeight/2 - 8, frameWidth + 16, frameHeight + 16, 16);
    this.frame.fill({ color: 0x5D4037 }); // Dark brown
    
    // Inner background - dark brown with transparency for reels to show through
    this.frame.roundRect(-frameWidth/2 + 5, -frameHeight/2 + 5, frameWidth - 10, frameHeight - 10, 8);
    this.frame.fill({ color: 0x4E342E, alpha: 0.95 }); // Dark brown
    
    // Gold border (Ethiopian gold trim)
    this.frame.roundRect(-frameWidth/2, -frameHeight/2, frameWidth, frameHeight, 10);
    this.frame.stroke({ color: 0xB8860B, width: 6 }); // Dark goldenrod
    
    this.frame.zIndex = 100; // On top of symbols
  }
  
  private async createButtonsAsync(): Promise<void> {
    await this.loadUITextures();
    this.createButtons();
  }
  
  private async loadUITextures(): Promise<void> {
    try {
      // Load UI textures
      const spinTexture = await Assets.load('/assets/ui-2/spin-btn.png');
      const plusTexture = await Assets.load('/assets/ui-2/plus-btn.png');
      const minusTexture = await Assets.load('/assets/ui-2/minus-btn.png');
      const infoTexture = await Assets.load('/assets/ui-2/info-btn.png');
      const menuTexture = await Assets.load('/assets/ui-2/menu-btn.png');
      const autoSpinTexture = await Assets.load('/assets/ui-2/auto-spin.png');
      
      this.uiTextures.set('spin', spinTexture);
      this.uiTextures.set('plus', plusTexture);
      this.uiTextures.set('minus', minusTexture);
      this.uiTextures.set('info', infoTexture);
      this.uiTextures.set('menu', menuTexture);
      this.uiTextures.set('autoSpin', autoSpinTexture);
      
      console.log('UI textures loaded:', Array.from(this.uiTextures.keys()));
    } catch (error) {
      console.error('Failed to load UI textures:', error);
      // Fall back to regular buttons if textures fail to load
    }
  }
  
  private createHistoryPopup(): void {
    this.historyPopup = new HistoryPopup();
    this.container.addChild(this.historyPopup);
    
    // Set callbacks for pagination
    this.historyPopup.onNext(() => this.loadNextHistoryPage());
    this.historyPopup.onPrevious(() => this.loadPrevHistoryPage());
    
    // Load initial history
    this.loadBetHistory();
  }
  
  private async loadBetHistory(): Promise<void> {
    try {
      await this.gameStore.loadBetHistory(1, 10);
      this.updateHistoryDisplay();
    } catch (error) {
      console.error('Failed to load bet history:', error);
    }
  }
  
  private async loadNextHistoryPage(): Promise<void> {
    try {
      await this.gameStore.loadBetHistory(this.gameStore.historyPage + 1, 10);
      this.updateHistoryDisplay();
    } catch (error) {
      console.error('Failed to load next page:', error);
    }
  }
  
  private async loadPrevHistoryPage(): Promise<void> {
    try {
      const prevPage = Math.max(1, this.gameStore.historyPage - 1);
      await this.gameStore.loadBetHistory(prevPage, 10);
      this.updateHistoryDisplay();
    } catch (error) {
      console.error('Failed to load previous page:', error);
    }
  }
  
  public updateHistoryDisplay(): void {
    const bets = this.gameStore.betHistory;
    console.log('Updating history display with', bets.length, 'bets');
    this.historyPopup.setBets(bets);
  }
  
  private createAutoSpinPopup(): void {
    this.autoSpinPopup = new AutoSpinPopup();
    this.container.addChild(this.autoSpinPopup);
    
    this.autoSpinPopup.onConfirm((rounds: number) => {
      if (this.onAutoSpinCallback) {
        this.onAutoSpinCallback(rounds);
      }
    });
  }
  
  private createNotification(): void {
    this.notification = new Notification();
    this.container.addChild(this.notification);
  }
  
  private createBackground(): void {
    const width = 768;
    const height = 1024;
    
    const gradient = this.background;
    gradient.clear();
    
    // Create darker traditional Ethiopian paper-like background
    // Darker brown gradient from top to bottom
    for (let i = 0; i < height; i++) {
      const ratio = i / height;
      // Gradient from medium brown (top) to dark brown (bottom)
      const r = Math.floor(180 - ratio * 50);   // 180 → 130
      const g = Math.floor(150 - ratio * 45);   // 150 → 105
      const b = Math.floor(130 - ratio * 40);   // 130 → 90
      const color = (r << 16) | (g << 8) | b;
      
      gradient.rect(-width/2, -height/2 + i, width, 1);
      gradient.fill({ color });
    }
  }
  
  private createTexts(): void {
    // Top bar background removed for cleaner look
    
    // Balance container - elegant card design with more padding
    const balanceContainer = new Graphics();
    
    // Outer shadow/glow effect
    balanceContainer.roundRect(-298, -503, 296, 54, 12);
    balanceContainer.fill({ color: 0x000000, alpha: 0.3 });
    
    // Main background with gradient effect
    balanceContainer.roundRect(-300, -505, 300, 58, 12);
    balanceContainer.fill({ color: 0x1A0F0A }); // Very dark brown
    
    // Inner gradient (lighter strip)
    balanceContainer.roundRect(-298, -503, 296, 54, 10);
    balanceContainer.fill({ color: 0x2E1810 }); // Dark brown
    
    // Highlight at top - reduced gloss
    balanceContainer.roundRect(-298, -503, 296, 24, 10);
    balanceContainer.fill({ color: 0x4A3428, alpha: 0.2 }); // Lighter brown highlight
    
    // Gold border (decorative frame)
    balanceContainer.roundRect(-300, -505, 300, 58, 12);
    balanceContainer.stroke({ color: 0xFFD700, width: 2 }); // Gold
    
    // Inner gold line (subtle)
    balanceContainer.roundRect(-296, -501, 292, 50, 10);
    balanceContainer.stroke({ color: 0xDAA520, width: 1, alpha: 0.6 }); // Darker gold
    
    balanceContainer.zIndex = 10;
    this.container.addChild(balanceContainer);
    
    // Balance icon (coin/money emoji) - larger and more prominent
    const balanceIcon = new Text({ 
      text: '💰', 
      style: new TextStyle({
        fontFamily: 'Spenbeb, Georgia, serif',
        fontSize: 32
      })
    });
    balanceIcon.anchor.set(0, 0.5);
    balanceIcon.x = -285;
    balanceIcon.y = -476;
    balanceIcon.zIndex = 11;
    this.container.addChild(balanceIcon);
    
    // Balance label (small "BALANCE" text above the value)
    const balanceLabel = new Text({ 
      text: 'BALANCE', 
      style: new TextStyle({
        fontFamily: 'Spenbeb, Georgia, serif',
        fontSize: 11,
        fontWeight: 'bold',
        fill: 0xDAA520, // Darker gold
        letterSpacing: 2
      })
    });
    balanceLabel.anchor.set(0, 0);
    balanceLabel.x = -245;
    balanceLabel.y = -498;
    balanceLabel.zIndex = 11;
    this.container.addChild(balanceLabel);
    
    // Balance text - larger, more prominent with elegant styling
    const counterStyle = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 24,
      fontWeight: '600',
      fill: 0xFFFF99, // Bright shiny yellow
      stroke: { color: 0x2E1810, width: 3 }, // Very dark brown outline
      dropShadow: {
        color: 0xFFFF00,
        blur: 4,
        distance: 0,
        alpha: 0.3
      }
    });
    
    this.balanceText = new Text({ text: '10,000 ETB', style: counterStyle });
    this.balanceText.anchor.set(0, 0);
    this.balanceText.x = -245;
    this.balanceText.y = -488;
    this.balanceText.zIndex = 11;
    this.container.addChild(this.balanceText);
    
    // Bet amount - gold text
    const betStyle = new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 28,
      fontWeight: 'bold',
      fill: 0xFFD700, // Gold
      stroke: { color: 0x3E2723, width: 2 }, // Very dark brown outline
      dropShadow: {
        color: 0x5D4037,
        blur: 3,
        distance: 2
      }
    });
    
    this.betText = new Text({ text: '10', style: betStyle });
    this.betText.anchor.set(0.5);
    this.betText.x = 0;
    this.betText.y = 220;
    this.betText.zIndex = 10;
    this.container.addChild(this.betText);
    
    // Win display - larger gold text
    this.winText = new Text({ text: '', style: new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 44,
      fontWeight: 'bold',
      fill: 0xFFD700, // Gold
      stroke: { color: 0x4A3728, width: 4 }, // Dark brown outline
      dropShadow: {
        color: 0xDAA520,
        blur: 6,
        distance: 3
      }
    })});
    this.winText.anchor.set(0.5);
    this.winText.x = 0;
    this.winText.y = -50;
    this.winText.visible = false;
    this.winText.zIndex = 10;
    this.container.addChild(this.winText);
    
    // Free spins display
    this.freeSpinsText = new Text({ text: '', style: new TextStyle({
      fontFamily: 'Spenbeb, Georgia, serif',
      fontSize: 28,
      fontWeight: 'bold',
      fill: 0xFFD700, // Gold
      stroke: { color: 0x4A3728, width: 2 }, // Dark brown outline
      dropShadow: {
        color: 0x000000,
        blur: 3,
        distance: 2
      }
    })});
    this.freeSpinsText.anchor.set(0.5);
    this.freeSpinsText.x = 0;
    this.freeSpinsText.y = -440;
    this.freeSpinsText.zIndex = 10;
    this.container.addChild(this.freeSpinsText);
  }
  
  private createButtons(): void {
    // Check if UI textures are loaded, use image buttons or fall back to regular buttons
    const useImageButtons = this.uiTextures.has('spin');
    
    if (useImageButtons) {
      console.log('Using image-based buttons from ui-2 folder');
      
      // Spin button with image - natural size: 404x253, scale to fit
      const spinTexture = this.uiTextures.get('spin')!;
      const spinScale = 160 / 404; // Scale to approximately 160px width while preserving aspect ratio
      this.spinButton = new ImageButton(spinTexture, '', { scale: spinScale });
      this.spinButton.x = 0;
      this.spinButton.y = 340;
      this.spinButton.zIndex = 10;
      this.spinButton.setCallback(() => {
        this.audioManager.play('click');
        if (this.onSpinCallback) this.onSpinCallback();
      });
      this.container.addChild(this.spinButton);
      
      // Bet decrease button with image - natural size: 102x89
      const minusTexture = this.uiTextures.get('minus')!;
      const minusScale = 0.6; // 20% larger than before
      this.betDownButton = new ImageButton(minusTexture, '', { scale: minusScale, holdToRepeat: true });
      this.betDownButton.x = -90;
      this.betDownButton.y = 220;
      this.betDownButton.zIndex = 10;
      this.betDownButton.setCallback(() => {
        this.audioManager.play('click');
        if (this.onBetDownCallback) this.onBetDownCallback();
      });
      this.container.addChild(this.betDownButton);
      
      // Bet increase button with image - natural size: 102x89
      const plusTexture = this.uiTextures.get('plus')!;
      const plusScale = 0.6; // 20% larger than before
      this.betUpButton = new ImageButton(plusTexture, '', { scale: plusScale, holdToRepeat: true });
      this.betUpButton.x = 90;
      this.betUpButton.y = 220;
      this.betUpButton.zIndex = 10;
      this.betUpButton.setCallback(() => {
        this.audioManager.play('click');
        if (this.onBetUpCallback) this.onBetUpCallback();
      });
      this.container.addChild(this.betUpButton);
      
      // Info button with image - natural size: 280x280
      const infoTexture = this.uiTextures.get('info')!;
      const infoScale = 0.35; // Same scale as history button
      this.infoButton = new ImageButton(infoTexture, '', { scale: infoScale });
      this.infoButton.x = 170;
      this.infoButton.y = 340;
      this.infoButton.zIndex = 10;
      this.infoButton.setCallback(() => {
        this.audioManager.play('click');
        this.infoPopup.toggle();
      });
      this.container.addChild(this.infoButton);
    } else {
      console.log('Falling back to regular buttons (ui textures not loaded)');
      
      // Use regular buttons as fallback
      this.spinButton = new Button('🎰 SPIN', 160, 60, {
        backgroundColor: 0x8B4513,
        hoverColor: 0xA0522D,
        pressedColor: 0x654321,
        glowColor: 0xFFD700,
        glowSize: 20,
        fontSize: 24
      });
      this.spinButton.x = 0;
      this.spinButton.y = 340;
      this.spinButton.zIndex = 10;
      this.spinButton.setCallback(() => {
        this.audioManager.play('click');
        if (this.onSpinCallback) this.onSpinCallback();
      });
      this.container.addChild(this.spinButton);
      
      this.betDownButton = new Button('−', 36, 36, {
        backgroundColor: 0x654321,
        hoverColor: 0x8B4513,
        fontSize: 22
      });
      this.betDownButton.x = -90;
      this.betDownButton.y = 220;
      this.betDownButton.zIndex = 10;
      this.betDownButton.setCallback(() => {
        this.audioManager.play('click');
        if (this.onBetDownCallback) this.onBetDownCallback();
      });
      this.container.addChild(this.betDownButton);
      
      this.betUpButton = new Button('+', 36, 36, {
        backgroundColor: 0x654321,
        hoverColor: 0x8B4513,
        fontSize: 22
      });
      this.betUpButton.x = 90;
      this.betUpButton.y = 220;
      this.betUpButton.zIndex = 10;
      this.betUpButton.setCallback(() => {
        this.audioManager.play('click');
        if (this.onBetUpCallback) this.onBetUpCallback();
      });
      this.container.addChild(this.betUpButton);
      
      this.infoButton = new Button('ℹ️ Info', 55, 55, {
        backgroundColor: 0x654321,
        hoverColor: 0x8B4513,
        fontSize: 20
      });
      this.infoButton.x = 170;
      this.infoButton.y = 340;
      this.infoButton.zIndex = 10;
      this.infoButton.setCallback(() => {
        this.audioManager.play('click');
        this.infoPopup.toggle();
      });
      this.container.addChild(this.infoButton);
    }
    
    // Auto-spin button
    const autoSpinTexture = this.uiTextures.get('autoSpin');
    if (autoSpinTexture) {
      // Auto-spin button with image
      const autoSpinScale = 0.5; // Scale to 50% of original size
      this.autoSpinButton = new ImageButton(autoSpinTexture, '', { scale: autoSpinScale });
      this.autoSpinButton.x = -170;
      this.autoSpinButton.y = 340;
      this.autoSpinButton.zIndex = 10;
      this.autoSpinButton.setCallback(() => {
        this.audioManager.play('click');
        if (this.isAutoSpinning) {
          // Stop auto spin
          this.stopAutoSpin();
        } else {
          // Open auto spin popup
          this.autoSpinPopup.open();
        }
      });
      this.container.addChild(this.autoSpinButton);
    } else {
      // Fallback to regular button
      this.autoSpinButton = new Button('🔄 Auto', 80, 45, {
        backgroundColor: 0x654321,
        hoverColor: 0x8B4513,
        fontSize: 16
      });
      this.autoSpinButton.x = -170;
      this.autoSpinButton.y = 340;
      this.autoSpinButton.zIndex = 10;
      this.autoSpinButton.setCallback(() => {
        this.audioManager.play('click');
        if (this.isAutoSpinning) {
          // Stop auto spin
          this.stopAutoSpin();
        } else {
          // Open auto spin popup
          this.autoSpinPopup.open();
        }
      });
      this.container.addChild(this.autoSpinButton);
    }
    
    // History button (use menu image button)
    const menuTexture = this.uiTextures.get('menu');
    if (menuTexture) {
      // Menu button is 138x137, scale it down to be much smaller
      const menuScale = 0.35; // Make it 35% of original size (about 48x48 pixels)
      this.historyButton = new ImageButton(menuTexture, '', { scale: menuScale });
      this.historyButton.x = 255;
      this.historyButton.y = 340;
      this.historyButton.zIndex = 10;
      this.historyButton.setCallback(() => {
        this.audioManager.play('click');
        this.loadBetHistory();
        this.historyPopup.toggle();
      });
      this.container.addChild(this.historyButton);
    } else {
      // Fallback to regular button
      this.historyButton = new Button('📜', 55, 55, {
        backgroundColor: 0xDAA520,
        hoverColor: 0xFFD700,
        fontSize: 26
      });
      this.historyButton.x = 255;
      this.historyButton.y = 340;
      this.historyButton.zIndex = 10;
      this.historyButton.setCallback(() => {
        this.audioManager.play('click');
        this.loadBetHistory();
        this.historyPopup.toggle();
      });
      this.container.addChild(this.historyButton);
    }
  }
  
  private createInfoPopup(): void {
    this.infoPopup = new InfoPopup();
    this.container.addChild(this.infoPopup);
  }
  
  public updateBalance(balance: number, currency: string): void {
    // Format balance with thousands separator and currency
    const formattedBalance = balance.toLocaleString();
    this.balanceText.text = `${formattedBalance} ${currency}`;
  }
  
  public updateBet(bet: number): void {
    this.betText.text = `${bet}`;
  }
  
  public showInsufficientBalance(): void {
    this.notification.showInsufficientBalance();
  }
  
  public showError(message: string): void {
    this.notification.show(message, 3000);
  }
  
  public updatePaylines(count: number): void {
    // Removed as per user request - no line selectors
  }
  
  public showWin(amount: number): void {
    const prefix = amount >= 500 ? '🎉 BIG WIN! ' : amount >= 100 ? '⭐ ' : '✨ ';
    this.winText.text = `${prefix}${amount.toLocaleString()}${amount >= 500 ? '!' : ''}`;
    this.winText.visible = true;
    this.winText.scale.set(0.5);
    this.winText.alpha = 0;
    
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / 300, 1);
      
      this.winText.scale.set(0.5 + progress * 0.5);
      this.winText.alpha = progress;
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    animate();
    
    setTimeout(() => {
      this.winText.visible = false;
    }, 2000);
    
    if (amount >= 500) {
      this.audioManager.play('bigWin');
    } else if (amount > 0) {
      this.audioManager.play('win');
    }
  }
  
  public updateFreeSpins(count: number): void {
    if (count > 0) {
      this.freeSpinsText.text = `🎁 FREE SPINS: ${count}`;
      this.freeSpinsText.visible = true;
    } else {
      this.freeSpinsText.visible = false;
    }
  }
  
  public setSpinEnabled(enabled: boolean): void {
    if (this.spinButton instanceof Button) {
      this.spinButton.setEnabled(enabled);
    } else if (this.spinButton instanceof ImageButton) {
      this.spinButton.setEnabled(enabled);
    }
  }
  
  public update(delta: number): void {
    if (this.spinButton instanceof Button) {
      this.spinButton.update(delta);
    } else if (this.spinButton instanceof ImageButton) {
      this.spinButton.update(delta);
    }
    
    // Animate auto spin button when spinning
    if (this.isAutoSpinning) {
      this.autoSpinAnimationTime += delta * 0.05;
      const scale = 1 + Math.sin(this.autoSpinAnimationTime) * 0.15; // Pulsing effect (1.0 to 1.15)
      if (this.autoSpinButton instanceof ImageButton) {
        // ImageButton uses sprite.scale internally
        const baseScale = 0.5; // The original scale used in constructor
        this.autoSpinButton.scale.set(scale);
      }
    }
    
    if (this.autoSpinButton instanceof Button) {
      this.autoSpinButton.update(delta);
    } else if (this.autoSpinButton instanceof ImageButton) {
      this.autoSpinButton.update(delta);
    }
    
    if (this.betUpButton instanceof Button) {
      this.betUpButton.update(delta);
    } else if (this.betUpButton instanceof ImageButton) {
      this.betUpButton.update(delta);
    }
    
    if (this.betDownButton instanceof Button) {
      this.betDownButton.update(delta);
    } else if (this.betDownButton instanceof ImageButton) {
      this.betDownButton.update(delta);
    }
    
    if (this.infoButton instanceof Button) {
      this.infoButton.update(delta);
    } else if (this.infoButton instanceof ImageButton) {
      this.infoButton.update(delta);
    }
    
    if (this.historyButton instanceof Button) {
      this.historyButton.update(delta);
    } else if (this.historyButton instanceof ImageButton) {
      this.historyButton.update(delta);
    }
  }
  
  public onSpin(callback: () => void): void {
    this.onSpinCallback = callback;
  }
  
  public onBetUp(callback: () => void): void {
    this.onBetUpCallback = callback;
  }
  
  public onBetDown(callback: () => void): void {
    this.onBetDownCallback = callback;
  }
  
  public onAutoSpin(callback: (rounds: number) => void): void {
    this.onAutoSpinCallback = callback;
  }
  
  public onStopAutoSpin(callback: () => void): void {
    this.onStopAutoSpinCallback = callback;
  }
  
  public isAutoSpinActive(): boolean {
    return this.isAutoSpinning;
  }
  
  public stopAutoSpin(): void {
    this.isAutoSpinning = false;
    this.autoSpinAnimationTime = 0;
    
    // Reset button scale
    if (this.autoSpinButton instanceof ImageButton) {
      this.autoSpinButton.scale.set(1);
    }
    
    if (this.autoSpinButton instanceof Button) {
      this.autoSpinButton.setText('🔄 Auto');
    }
    
    if (this.onStopAutoSpinCallback) {
      this.onStopAutoSpinCallback();
    }
  }
  
  public startAutoSpinUI(): void {
    this.isAutoSpinning = true;
    this.autoSpinAnimationTime = 0;
    
    if (this.autoSpinButton instanceof Button) {
      this.autoSpinButton.setText('⏸ Stop');
    }
  }
  
  public showAutoSpinRounds(remaining: number, total: number): void {
    if (remaining > 0) {
      this.notification.show(`Auto Spin: ${remaining}/${total}`, 1500);
    }
  }
}